// Define custom element for the modal
class ShopibroPopupModal extends HTMLElement {
    constructor() {
        super();
        this._isOpen = false;
        this._swiper = null;
    }

    connectedCallback() {
        // Create the elements when connected to DOM
        this._createElements();
        this._addEventListeners();
    }

    // Create the modal structure using template literals
    _createElements() {
        // Create the base HTML structure
        this.innerHTML = `
            <div class="shopibro-modal-content">
                <button class="shopibro-modal-close" aria-label="Close modal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="37" height="37" viewBox="0 0 37 37" fill="none">
                        <circle cx="18.75" cy="18.75" r="15" stroke="#1C274C" stroke-width="2.25"/>
                        <path d="M22.5 15L15 22.5M15 15L22.4999 22.5" stroke="#1C274C" stroke-width="2.25" stroke-linecap="round"/>
                    </svg>
                </button>
                
                <div class="shopibro-modal-header">
                    <div class="shopibro-modal-title"></div>
                    <h2 class="shopibro-modal-heading"></h2>
                </div>
                
                <div class="shopibro-modal-body">
                    <div class="shopibro-image-carousel swiper">
                        <div class="swiper-wrapper"></div>
                        <div class="swiper-pagination"></div>
                        <div class="swiper-button-prev">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="11" viewBox="0 0 14 11" fill="none">
                                <path d="M12.8409 5.75007H0.659124M0.659124 5.75007L5.22731 1.18188M0.659124 5.75007L5.22731 10.3182" stroke="#8E8E8E" stroke-width="1.14205" stroke-linecap="round" stroke-linejoin="round"></path>
                            </svg>
                        </div>
                        <div class="swiper-button-next">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="11" viewBox="0 0 14 11" fill="none">
                                <path d="M0.658936 5.75007H12.8408M12.8408 5.75007L8.27257 1.18188M12.8408 5.75007L8.27257 10.3182" stroke="#8E8E8E" stroke-width="1.14205" stroke-linecap="round" stroke-linejoin="round"></path>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Store references to key elements
        this._modalContent = this.querySelector('.shopibro-modal-content');
        this._titleEl = this.querySelector('.shopibro-modal-title');
        this._headingEl = this.querySelector('.shopibro-modal-heading');
        this._carouselContainer = this.querySelector('.shopibro-image-carousel');
        this._swiperWrapper = this.querySelector('.swiper-wrapper');
        this._pagination = this.querySelector('.swiper-pagination');
        this._prevButton = this.querySelector('.swiper-button-prev');
        this._nextButton = this.querySelector('.swiper-button-next');
        this._modalBody = this.querySelector('.shopibro-modal-body');

        // Initially hide navigation and pagination
        this._hideNavigation();
    }

    // Hide navigation and pagination
    _hideNavigation() {
        if (this._prevButton) this._prevButton.style.display = 'none';
        if (this._nextButton) this._nextButton.style.display = 'none';
        if (this._pagination) this._pagination.style.display = 'none';
    }

    // Show navigation and pagination based on settings
    _showNavigation(settings) {
        if (settings.navigation) {
            if (this._prevButton) this._prevButton.style.display = '';
            if (this._nextButton) this._nextButton.style.display = '';
        }

        if (settings.pagination) {
            if (this._pagination) this._pagination.style.display = '';
        }
    }

    // Add event listeners
    _addEventListeners() {
        // Close on clicking the close button
        const closeButton = this.querySelector('.shopibro-modal-close');
        if (closeButton) {
            closeButton.addEventListener('click', () => {
                this.close();
            });
        }

        // Close on clicking outside the modal content
        this.addEventListener('click', (event) => {
            if (event.target === this) {
                this.close();
            }
        });

        // Close on pressing Escape key
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this._isOpen) {
                this.close();
            }
        });
    }

    // Initialize Swiper
    _initializeSwiper(enableLoop, swiperSettings) {
        // Check if Swiper is available
        if (typeof Swiper === 'undefined') {
            console.error('Swiper is not loaded. Make sure to include Swiper JS library.');
            return;
        }

        // Destroy existing swiper instance if it exists
        if (this._swiper && typeof this._swiper.destroy === 'function') {
            this._swiper.destroy(true, true);
            this._swiper = null;
        }

        // Apply swiper settings
        const settings = swiperSettings || {};

        // Show/hide navigation based on settings
        this._showNavigation(settings);

        // Create new Swiper instance with merged settings
        const swiperConfig = {
            slidesPerView: settings.slidesPerView || 2.2,
            spaceBetween: settings.spaceBetween || 20,
            loop: settings.loop !== undefined ? settings.loop : enableLoop,
            autoplay: settings.autoplay || (enableLoop ? {
                delay: 5000,
                disableOnInteraction: false
            } : false),
        };

        // Only add navigation config if navigation is enabled
        if (settings.navigation) {
            swiperConfig.navigation = {
                nextEl: this._nextButton,
                prevEl: this._prevButton
            };
        }

        // Only add pagination config if pagination is enabled
        if (settings.pagination) {
            swiperConfig.pagination = {
                el: this._pagination,
                clickable: true
            };
        }

        console.log("Initializing Swiper with config:", swiperConfig);

        // Initialize Swiper with the config
        this._swiper = new Swiper(this._carouselContainer, swiperConfig);
    }

    // Set modal content based on data attributes
    updateContent(data) {
        if (!data) return;

        // Set title and heading
        if (this._titleEl) this._titleEl.textContent = data.title || '';
        if (this._headingEl) this._headingEl.textContent = data.heading || '';

        // Set up the carousel with features
        if (this._swiperWrapper && data.features && Array.isArray(data.features)) {
            this._swiperWrapper.innerHTML = '';

            // Create a slide for each feature with its corresponding image
            data.features.forEach((feature, index) => {
                const slideHtml = `
                    <div class="swiper-slide shopibro-feature-slide">
                        <div class="shopibro-feature-image">
                            ${data.images && data.images[index] ?
                    `<img src="${data.images[index]}" alt="">` :
                    ''}
                        </div>
                        <div class="shopibro-feature-content">
                            <div class="shopibro-feature-title">${feature.title || ''}</div>
                            <div class="shopibro-feature-description">${feature.description || ''}</div>
                        </div>
                    </div>
                `;

                this._swiperWrapper.insertAdjacentHTML('beforeend', slideHtml);
            });

            // Get custom swiper settings
            let swiperSettings = {};
            if (data.swiperSettings) {
                // Clone the settings to avoid modifying the original
                swiperSettings = JSON.parse(JSON.stringify(data.swiperSettings));

                // Log for debugging
                console.log('Swiper settings:', swiperSettings);
            }

            // Initialize Swiper
            this._initializeSwiper(false, swiperSettings);
        }

        // Set sizes - Only create and append if sizes exist
        if (data.sizes && Array.isArray(data.sizes) && data.sizes.length > 0) {
            // Check if sizes section already exists
            let sizesSection = this.querySelector('.shopibro-sizes');

            // Create it if it doesn't exist
            if (!sizesSection) {
                this._modalBody.insertAdjacentHTML('beforeend', '<div class="shopibro-sizes"></div>');
                sizesSection = this.querySelector('.shopibro-sizes');
            }

            // Build the sizes HTML
            let sizesHtml = `
                <div class="shopibro-size-label">Available Size</div>
                <div class="shopibro-size-options">
            `;

            data.sizes.forEach(size => {
                sizesHtml += `
                    <div class="shopibro-size">
                        <div class="shopibro-size-name">${size.name || ''}</div>
                        <div class="shopibro-size-dimensions">${size.dimensions || ''}</div>
                    </div>
                `;
            });

            sizesHtml += '</div>';
            sizesSection.innerHTML = sizesHtml;
        } else {
            // Remove sizes section if it exists but no sizes data
            const sizesSection = this.querySelector('.shopibro-sizes');
            if (sizesSection) {
                sizesSection.remove();
            }
        }
    }

    // Open the modal
    open() {
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
        this._isOpen = true;
        this.setAttribute('class', 'active');

        // Update swiper if it exists
        if (this._swiper && typeof this._swiper.update === 'function') {
            setTimeout(() => {
                this._swiper.update();
            }, 10);
        }
    }

    // Close the modal
    close() {
        document.body.style.overflow = ''; // Restore background scrolling
        this._isOpen = false;
        this.removeAttribute('class');

        // Destroy Swiper instance to prevent memory leaks
        if (this._swiper && typeof this._swiper.destroy === 'function') {
            this._swiper.destroy(true, true);
            this._swiper = null;
        }
    }
}

// Define custom element for the modal opener
class ShopibroPopupModalOpener extends HTMLElement {
    connectedCallback() {
        // Make sure the element is clickable by default
        if (this.style.cursor !== 'pointer') {
            this.style.cursor = 'pointer';
        }

        // Add event listeners when the element is connected to the DOM
        this.addEventListener('click', this._handleClick.bind(this));
    }

    // Parse data attributes
    _getModalData() {
        let features = [];
        let sizes = [];
        let images = [];
        let swiperSettings = null;

        try {
            if (this.hasAttribute('data-features')) {
                features = JSON.parse(this.getAttribute('data-features'));
                console.log('kita', features)
            }
        } catch (e) {
            console.error('Error parsing features JSON:', e);
        }

        try {
            if (this.hasAttribute('data-sizes')) {
                sizes = JSON.parse(this.getAttribute('data-sizes'));
            }
        } catch (e) {
            console.error('Error parsing sizes JSON:', e);
        }

        try {
            if (this.hasAttribute('data-images')) {
                // Parse images as JSON array if it starts with [
                if (this.getAttribute('data-images').trim().startsWith('[')) {
                    images = JSON.parse(this.getAttribute('data-images'));
                } else {
                    // Otherwise, split by comma
                    images = this.getAttribute('data-images').split(',').map(url => url.trim()).filter(url => url);
                }
            }
        } catch (e) {
            console.error('Error parsing images:', e);
        }

        try {
            if (this.hasAttribute('data-swiper-settings')) {
                swiperSettings = JSON.parse(this.getAttribute('data-swiper-settings'));
            }
        } catch (e) {
            console.error('Error parsing swiper settings JSON:', e);
        }

        return {
            title: this.getAttribute('data-title'),
            heading: this.getAttribute('data-heading'),
            images: images,
            features,
            sizes,
            swiperSettings
        };
    }

    // Handle click event
    _handleClick() {
        // Get modal data
        const modalData = this._getModalData();

        // Get or create modal instance
        let modal = document.querySelector('shopibro-popup-modal');

        if (!modal) {
            modal = document.createElement('shopibro-popup-modal');
            document.body.appendChild(modal);

            // Wait for the element to be connected to the DOM
            if (document.readyState === 'complete' || document.readyState === 'interactive') {
                // DOM is ready, update content and open
                setTimeout(() => {
                    modal.updateContent(modalData);
                    modal.open();
                }, 0);
            } else {
                // Wait for DOM to be ready
                document.addEventListener('DOMContentLoaded', () => {
                    modal.updateContent(modalData);
                    modal.open();
                });
            }
        } else {
            // Modal already exists
            modal.updateContent(modalData);
            modal.open();
        }
    }
}

// Register custom elements when the DOM is fully loaded
if (typeof customElements !== 'undefined') {
    customElements.define('shopibro-popup-modal', ShopibroPopupModal);
    customElements.define('shopibro-popup-modal-opener', ShopibroPopupModalOpener);
} else {
    console.error('Custom Elements not supported in this browser');
}